import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from "@mui/material";
import { ChevronDown } from "lucide-react";
import CustomTextField from "./CustomTextField";
import {
  ALTERNATIVE_LANGUAGES,
  TITLE_PASTE_ORDER_HINT,
  parseTitlePaste,
} from "./alternativeLanguages";

const AlternativeNamesFields = ({
  value,
  onChange,
  maxLength,
  onEnglishTitleChange,
}) => {
  const names = value || {};
  const hasAny = ALTERNATIVE_LANGUAGES.some(({ code }) =>
    (names[code] || "").trim()
  );
  const [expanded, setExpanded] = useState(hasAny);
  const [pasteValue, setPasteValue] = useState("");
  const [pasteHint, setPasteHint] = useState("");

  const handleApplyPaste = () => {
    if (!pasteValue.trim()) {
      setPasteHint("Paste comma-separated titles first.");
      return;
    }
    const { englishTitle, alternativeNames, expectedCount, receivedCount } =
      parseTitlePaste(pasteValue);
    if (typeof onEnglishTitleChange === "function") {
      onEnglishTitleChange(englishTitle);
    }
    onChange(alternativeNames);
    setExpanded(true);
    if (receivedCount !== expectedCount) {
      setPasteHint(
        `Applied ${Math.min(receivedCount, expectedCount)} of ${expectedCount} slots (got ${receivedCount}). Order: ${TITLE_PASTE_ORDER_HINT}`
      );
      return;
    }
    setPasteHint("Applied titles in paste order.");
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, next) => setExpanded(next)}
      disableGutters
      elevation={0}
      sx={{
        mt: 2,
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ChevronDown size={18} />}>
        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
          Alternative names
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="caption" sx={{ color: "#555", display: "block", mb: 1.5 }}>
          Paste all language titles in one comma-separated string, or edit a
          language below. Leave a field blank to show the English title.
        </Typography>
        <CustomTextField
          label="Paste titles (comma-separated)"
          value={pasteValue}
          onChange={(e) => {
            setPasteValue(e.target.value);
            if (pasteHint) setPasteHint("");
          }}
          multiline
          rows={2}
        />
        <Typography variant="caption" sx={{ color: "#555", display: "block", mt: 0.5 }}>
          Order: {TITLE_PASTE_ORDER_HINT}
        </Typography>
        <Box mt={1} mb={1.5}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleApplyPaste}
            sx={{ textTransform: "none" }}
          >
            Apply paste
          </Button>
          {pasteHint ? (
            <Typography
              variant="caption"
              sx={{ color: "#555", display: "block", mt: 0.75 }}
            >
              {pasteHint}
            </Typography>
          ) : null}
        </Box>
        {ALTERNATIVE_LANGUAGES.map(({ code, label }) => (
          <Box key={code} mt={1}>
            <CustomTextField
              label={label}
              value={names[code] || ""}
              onChange={(e) =>
                onChange({
                  ...names,
                  [code]: e.target.value,
                })
              }
              inputProps={{ maxLength }}
            />
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default AlternativeNamesFields;
